// Board-position classification. Pure. No React, no Three.js, no mutable
// state. THE single source of truth for "where is this cell on the grid":
// both perspective-independent facts (corner, outer column / row, edge)
// and perspective-dependent ones (back / front row, rank from back).
//
// Perspective model. The board has a fixed orientation; "back row" etc.
// are relative to a unit's controller:
//   • P1 (not rotated) sits at the HIGH-row edge. Back row = row (rows-1);
//     "forward" (toward the enemy) DECREASES the row index.
//   • P2 (rotated) is mirrored: back row = 0; forward INCREASES the row.
// This matches site anchoring (sitePlacement.siteSnapPoint. P1 anchors at
// the world-bottom / high-z edge).
//
// `grid` is the dims object from spawnConfig.getGameGrid: { cols, rows },
// where cols/rows are CELL COUNTS so the max index is count - 1.

/** The controller's back-row index (the outer row nearest them). */
export function homeRow(controllerRotated, rows) {
  return controllerRotated ? 0 : rows - 1;
}

/** Row-index step toward the enemy edge (-1 for P1, +1 for P2). */
export function forwardDir(controllerRotated) {
  return controllerRotated ? 1 : -1;
}

/**
 * The four corner cells of a grid, as { col, row }. Wayfaring Pilgrim's
 * "each corner of the realm", Cornerstone / Ninth Legion's "to any corner".
 */
export function cornerCells(grid) {
  if (!grid) return [];
  const maxCol = (grid.cols || 1) - 1;
  const maxRow = (grid.rows || 1) - 1;
  return [
    { col: 0, row: 0 },
    { col: maxCol, row: 0 },
    { col: 0, row: maxRow },
    { col: maxCol, row: maxRow },
  ];
}

/** True iff `cell` is one of the grid's four corners. */
export function isCornerCell(cell, grid) {
  if (!cell || !grid) return false;
  return cornerCells(grid).some((c) => c.col === cell.col && c.row === cell.row);
}

/** Leftmost or rightmost column (Forsaken, Howl from Beyond "outer columns"). */
export function isOuterColumn(cell, grid) {
  if (!cell || !grid) return false;
  return cell.col === 0 || cell.col === grid.cols - 1;
}

/** Topmost or bottommost row (perspective-independent edge row). */
export function isOuterRow(cell, grid) {
  if (!cell || !grid) return false;
  return cell.row === 0 || cell.row === grid.rows - 1;
}

/** Any outer ring cell (outer column OR outer row). */
export function isEdgeCell(cell, grid) {
  return isOuterColumn(cell, grid) || isOuterRow(cell, grid);
}

/** The controller's own back row (Round Table, "your back row" sites). */
export function isBackRow(cell, grid, controllerRotated) {
  if (!cell || !grid) return false;
  return cell.row === homeRow(controllerRotated, grid.rows);
}

/** The enemy-facing outer row from this controller's perspective. */
export function isFrontRow(cell, grid, controllerRotated) {
  if (!cell || !grid) return false;
  return cell.row === homeRow(!controllerRotated, grid.rows);
}

/**
 * 0-based distance from the controller's back row, increasing toward the
 * enemy. 0 = back row, rows-1 = front row. Drives "furthest forward"
 * comparisons (Vanguard Knights) without each caller re-deriving orientation.
 */
export function rankFromBack(cell, grid, controllerRotated) {
  if (!cell || !grid) return null;
  return Math.abs(cell.row - homeRow(controllerRotated, grid.rows));
}

/**
 * Full positional descriptor for a cell, from `controllerRotated`'s
 * perspective. The reusable query primitive: a card / condition / test asks
 * "where is this unit" once and reads off whichever facet it needs.
 */
export function classifyCell(cell, grid, controllerRotated = false) {
  if (!cell || !grid) return null;
  return {
    col: cell.col,
    row: cell.row,
    cols: grid.cols,
    rows: grid.rows,
    isCorner: isCornerCell(cell, grid),
    isOuterColumn: isOuterColumn(cell, grid),
    isOuterRow: isOuterRow(cell, grid),
    isEdge: isEdgeCell(cell, grid),
    isBackRow: isBackRow(cell, grid, controllerRotated),
    isFrontRow: isFrontRow(cell, grid, controllerRotated),
    rankFromBack: rankFromBack(cell, grid, controllerRotated),
  };
}

/**
 * Predicate dispatch for the `position_at` automation condition. `where` is
 * one of the perspective-independent / dependent classifications above.
 * Unknown keys fail closed.
 */
export function cellMatchesPosition(where, cell, grid, controllerRotated) {
  switch (where) {
    case 'corner': return isCornerCell(cell, grid);
    case 'outer_column': return isOuterColumn(cell, grid);
    case 'outer_row': return isOuterRow(cell, grid);
    case 'edge': return isEdgeCell(cell, grid);
    case 'back_row': return isBackRow(cell, grid, controllerRotated);
    case 'front_row': return isFrontRow(cell, grid, controllerRotated);
    default: return false;
  }
}
